import API from '../service'
import { types as errorTypes } from '@/modules/errors/store/types.ts'
import { STATUS_CREATED, STATUS_SUCCESS } from '@/utils/constants/statusConstants'
import { ENABLE_LOADING, DISABLE_LOADING } from '@/utils/constants/generalizeConstants'
import { types as successTypes } from '@/modules/successAlert/store/types'
import i18n from '@/plugins/vue-i18n'

const defaultPaymentsFilters = {
	search: null,
	orderBy: 'created_at',
	orderDirection: 'desc',
	created_at_from: null,
	created_at_to: null,
	withDeleted: null,
	payment_method_id: null,
}

export default {
	namespaced: true,
	state: {
		paymentMethods: {
			loading: false,
			data: [],
		},

		payments: {
			loading: false,
			data: [],
			meta: {
				page: 1,
				limit: 10,
				totalItems: 0,
				totalPages: 0,
				itemsPerPage: 10,
			},
		},

		paymentsFilters: { ...defaultPaymentsFilters },

		paymentTerms: ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'],
	},

	getters: {
		getIndex: (state) => {
			return ({ key, id }) => {
				return state[key].data.findIndex((item) => item.id === id)
			}
		},

		getID: (state) => {
			return ({ key, id }) => {
				return state[key].data.find((item) => item.id === id)
			}
		},

		getActivePaymentsFiltersCount: (state) => {
			const optionalKeys = [
				'search',
				'created_at_from',
				'created_at_to',
				'withDeleted',
				'payment_method_id',
			]

			return optionalKeys.filter(
				(key) => state.paymentsFilters[key] != null && state.paymentsFilters[key] !== ''
			).length
		},
	},

	mutations: {
		setPayments: (state, payments) => (state.payments.data = payments),
		setPaymentsMeta: (state, meta) =>
			(state.payments.meta = { ...state.payments.meta, ...meta }),

		setPaymentMethods: (state, paymentMethods) => (state.paymentMethods.data = paymentMethods),

		setPaymentsFilter: (state, filters) => {
			state.paymentsFilters = { ...state.paymentsFilters, ...filters }
		},

		addPayment: (state, payment) => state.payments.data.push(payment),

		updatePayment: (state, { index, payment }) => state.payments.data.splice(index, 1, payment),

		setLoading: (state, { key, boolean }) => (state[key].loading = boolean),

		removePayment: (state, { index }) => {
			state.payments.data.splice(index, 1)
		},
	},

	actions: {
		async getAllPayments({ commit, state }, params = {}) {
			try {
				commit('setLoading', { key: 'payments', boolean: ENABLE_LOADING })

				// Pagamentos vinculados a entidade (ex.: pedido): não misturar com filtros da listagem
				// global (datas, método etc.), senão a API pode retornar vazio mesmo com dados no backend.
				const isEntityScoped =
					params.entity_id != null &&
					params.entity_id !== '' &&
					params.entity_type

				const merged = isEntityScoped
					? {
							...defaultPaymentsFilters,
							orderBy:
								state.paymentsFilters.orderBy ?? defaultPaymentsFilters.orderBy,
							orderDirection:
								state.paymentsFilters.orderDirection ??
								defaultPaymentsFilters.orderDirection,
							...params,
					  }
					: { ...state.paymentsFilters, ...params }

				if (merged.includeValueAggregations) {
					merged.includeValueAggregations = 'true'
				}

				const { status, data } = await API.getPayments(merged)

				if (status === STATUS_SUCCESS) {
					const payments = data?.data ?? data
					const meta = data?.meta ?? null

					commit('setPayments', Array.isArray(payments) ? payments : [])

					if (meta) {
						const metaPayload = {
							page: meta.currentPage ?? meta.page,
							limit: meta.itemsPerPage ?? meta.limit,
							totalItems: meta.totalItems,
							totalPages: meta.totalPages,
						}

						if (meta.payments_values) metaPayload.payments_values = meta.payments_values
						if (meta.payments_values_page)
							metaPayload.payments_values_page = meta.payments_values_page
						commit('setPaymentsMeta', metaPayload)
					}

					commit('setLoading', { key: 'payments', boolean: DISABLE_LOADING })
				}

				return status === STATUS_SUCCESS
			} catch (error) {
				commit('setLoading', { key: 'payments', boolean: DISABLE_LOADING })
				commit(`Errors/${errorTypes.ADD_ERROR}`, error.response, { root: true })
			}
		},

		async getManyEntityPayments({ commit }, { entity_ids, entity_type }) {
			try {
				commit('setLoading', { key: 'payments', boolean: ENABLE_LOADING })

				const { status, data: payments } = await API.getManyEntityPayments(
					entity_ids,
					entity_type
				)

				if (status === STATUS_SUCCESS) {
					commit('setPayments', payments)
					commit('setLoading', { key: 'payments', boolean: DISABLE_LOADING })
				}

				return status === STATUS_SUCCESS
			} catch (error) {
				commit('setLoading', { key: 'payments', boolean: DISABLE_LOADING })
				commit(`Errors/${errorTypes.ADD_ERROR}`, error.response, { root: true })
			}
		},

		async getPaymentMethods({ state, commit }) {
			try {
				if (!state.paymentMethods.data.length) {
					commit('setLoading', { key: 'paymentMethods', boolean: ENABLE_LOADING })

					const { status, data: paymentMethods } = await API.getPaymentMethods()

					if (status === STATUS_SUCCESS) {
						commit('setPaymentMethods', paymentMethods)
						commit('setLoading', { key: 'paymentMethods', boolean: DISABLE_LOADING })
					}

					return status === STATUS_SUCCESS
				}
			} catch (error) {
				commit('setLoading', { key: 'paymentMethods', boolean: DISABLE_LOADING })
				commit(`Errors/${errorTypes.ADD_ERROR}`, error.response, { root: true })
			}
		},

		async createPayment({ commit }, { payload }) {
			try {
				commit('setLoading', { key: 'payments', boolean: ENABLE_LOADING })

				const { status, data: payment } = await API.createPayment(payload)

				if (status === STATUS_CREATED) {
					commit('addPayment', payment)
					commit('setLoading', { key: 'payments', boolean: DISABLE_LOADING })
					commit(
						`Successes/${successTypes.ADD_SUCCESS}`,
						{
							title: i18n.t('modules.financial.feedback.success.create.payment'),
						},
						{ root: true }
					)
				}

				return status === STATUS_CREATED
			} catch (error) {
				commit('setLoading', { key: 'payments', boolean: DISABLE_LOADING })
				commit(`Errors/${errorTypes.ADD_ERROR}`, error.response, { root: true })
			}
		},

		async updatePayment({ getters, commit }, { paymentId, payload }) {
			try {
				commit('setLoading', { key: 'payments', boolean: ENABLE_LOADING })

				const { status, data: payment } = await API.updatePayment(paymentId, payload)

				if (status === STATUS_SUCCESS) {
					const index = getters.getIndex({ key: 'payments', id: paymentId })

					if (index > -1) {
						commit('updatePayment', { index, payment: payment })
						commit('setLoading', { key: 'payments', boolean: DISABLE_LOADING })
						commit(
							`Successes/${successTypes.ADD_SUCCESS}`,
							{
								title: i18n.t('modules.financial.feedback.success.update.payment'),
							},
							{ root: true }
						)
					}
				}

				return status === STATUS_SUCCESS
			} catch (error) {
				commit('setLoading', { key: 'payments', boolean: DISABLE_LOADING })
				commit(`Errors/${errorTypes.ADD_ERROR}`, error.response, { root: true })
			}
		},

		async removePayment({ getters, commit }, id) {
			try {
				commit('setLoading', { key: 'payments', boolean: ENABLE_LOADING })

				const { status } = await API.removePayment(id)

				if (status === STATUS_SUCCESS) {
					const index = getters.getIndex({ key: 'payments', id: id })

					if (index > -1) {
						commit('removePayment', { index: index })
						commit('setLoading', { key: 'payments', boolean: DISABLE_LOADING })
						commit(
							`Successes/${successTypes.ADD_SUCCESS}`,
							{
								title: i18n.t('modules.financial.feedback.success.remove.payment'),
							},
							{ root: true }
						)
					}
				}

				return status === STATUS_SUCCESS
			} catch (error) {
				commit('setLoading', { key: 'payments', boolean: DISABLE_LOADING })
				commit(`Errors/${errorTypes.ADD_ERROR}`, error.response, { root: true })
			}
		},
	},
}
